/**
 * Map get_discovery_feed → OpenVenue / PulseEvent for Home + Map.
 * Teaser rows: isTeaser=true; image falls back to category stock when RPC URL is null
 * (TE2 catalog often has empty photo_url — not an RPC bug).
 */
import { useQuery } from "@tanstack/react-query";
import fallbackBar from "@/assets/fallback-bar.jpg";
import fallbackClub from "@/assets/fallback-club.jpg";
import fallbackRooftop from "@/assets/fallback-rooftop.jpg";
import defaultImage from "@/assets/event-club.jpg";
import {
  fetchDiscoveryFeed,
  type DiscoveryTeaserRow,
} from "@/lib/discoveryFeed";
import type { EventCategory, OpenVenue, PulseEvent } from "@/lib/events";
import { stockImageAlternatesForVenue } from "@/lib/stockImageAlternates";

/** Public CDN category stock — used when photo_url / teaser_image_url is null. */
export const CATEGORY_STOCK_IMAGE: Record<EventCategory, string> = {
  Clubs: fallbackClub,
  Bars: fallbackBar,
  Rooftop: fallbackRooftop,
  Concerts: defaultImage,
};

function asCategory(value: unknown): EventCategory {
  const raw = String(value ?? "Bars").trim();
  const lower = raw.toLowerCase();
  if (lower === "clubs" || lower === "club") return "Clubs";
  if (lower === "concerts" || lower === "concert") return "Concerts";
  if (lower === "rooftop" || lower === "rooftops") return "Rooftop";
  if (lower === "bars" || lower === "bar") return "Bars";
  return "Bars";
}

export function resolveDiscoveryImage(
  url: string | null | undefined,
  category: string | null | undefined,
): string {
  const trimmed = url?.trim();
  if (trimmed) return trimmed;
  return CATEGORY_STOCK_IMAGE[asCategory(category)];
}

export function mapDiscoveryVenueFull(row: Record<string, unknown>): OpenVenue {
  const category = asCategory(row.category);
  const photo = row.photo_url != null ? String(row.photo_url) : null;
  const hours = row.opening_hours;
  return {
    id: String(row.id),
    name: String(row.name ?? row.display_name ?? ""),
    display_name: row.display_name != null ? String(row.display_name) : null,
    category,
    address: row.address != null ? String(row.address) : null,
    lat: typeof row.lat === "number" ? row.lat : null,
    lng: typeof row.lng === "number" ? row.lng : null,
    photo_url: photo,
    opening_hours: Array.isArray(hours) ? (hours as string[]) : [],
    website: row.website != null ? String(row.website) : null,
    city: String(row.city ?? ""),
    distance: "",
    distanceKm: 999,
    todayHours: "",
    image: resolveDiscoveryImage(photo, category),
    imageAlternates: stockImageAlternatesForVenue(String(row.id)),
    createdAt: row.created_at != null ? String(row.created_at) : null,
    isPopular: false,
    isTopPick: Boolean(row.is_top_pick),
    isNew: Boolean(row.is_new),
    isTeaser: false,
    googleRating: typeof row.google_rating === "number" ? row.google_rating : null,
    pulseRating: typeof row.pulse_rating === "number" ? row.pulse_rating : null,
    pulseRatingCount: typeof row.pulse_rating_count === "number" ? row.pulse_rating_count : 0,
    descriptionDe: row.description_de != null ? String(row.description_de) : null,
    descriptionEn: row.description_en != null ? String(row.description_en) : null,
    descriptionFr: row.description_fr != null ? String(row.description_fr) : null,
    descriptionIt: row.description_it != null ? String(row.description_it) : null,
  };
}

export function mapDiscoveryVenueTeaser(row: DiscoveryTeaserRow): OpenVenue {
  const category = asCategory(row.category);
  return {
    id: row.id,
    name: "",
    display_name: null,
    category,
    address: null,
    lat: null,
    lng: null,
    photo_url: row.teaser_image_url,
    opening_hours: [],
    website: null,
    city: "",
    distance: "",
    // Pass radius filters — caps already applied server-side.
    distanceKm: 0,
    todayHours: "",
    image: resolveDiscoveryImage(row.teaser_image_url, category),
    createdAt: null,
    isPopular: false,
    isTopPick: row.is_top_pick,
    isNew: false,
    isTeaser: true,
  };
}

function emptyPulseEvent(
  overrides: Partial<PulseEvent> & Pick<PulseEvent, "id" | "category" | "image">,
): PulseEvent {
  return {
    city: "",
    venue: "",
    venueId: null,
    venueAddress: null,
    name: "",
    time: "",
    price: "",
    distance: "",
    distanceKm: 0,
    imageAlternates: [],
    savedCount: 0,
    ticketsSold: 0,
    capacity: 0,
    popular: false,
    description: "",
    descriptionDe: null,
    descriptionEn: null,
    descriptionFr: null,
    descriptionIt: null,
    dressCode: "",
    opens: "",
    starts: "",
    ends: "",
    schedule: "",
    coords: { x: 50, y: 50 },
    lat: null,
    lng: null,
    entryPrice: null,
    currency: null,
    startsAtIso: null,
    endsAtIso: null,
    createdAt: null,
    rating: 0,
    pulseRating: null,
    pulseRatingCount: 0,
    musicStyle: "Pop",
    genres: [],
    gallery: [],
    isOutdoor: false,
    openingHours: [],
    isTopPick: false,
    isPopular: false,
    isNew: false,
    status: "published",
    storedMusicStyle: null,
    storedGenres: [],
    isTeaser: false,
    ...overrides,
  };
}

export function mapDiscoveryEventFull(row: Record<string, unknown>): PulseEvent {
  const category = asCategory(row.category ?? row.venue_category);
  const photo =
    row.photo_url != null
      ? String(row.photo_url)
      : row.venue_photo_url != null
        ? String(row.venue_photo_url)
        : null;
  return emptyPulseEvent({
    id: String(row.id),
    name: String(row.name ?? ""),
    venue: String(row.venue_name ?? ""),
    venueId: row.venue_id != null ? String(row.venue_id) : null,
    venueAddress: row.venue_address != null ? String(row.venue_address) : null,
    category,
    image: resolveDiscoveryImage(photo, category),
    lat: typeof row.venue_lat === "number" ? row.venue_lat : null,
    lng: typeof row.venue_lng === "number" ? row.venue_lng : null,
    startsAtIso: row.starts_at != null ? String(row.starts_at) : null,
    createdAt: row.created_at != null ? String(row.created_at) : null,
    isTopPick: Boolean(row.is_top_pick),
    isNew: Boolean(row.is_new),
    live: Boolean(row.is_live),
    distanceKm: 999,
    isTeaser: false,
  });
}

export function mapDiscoveryEventTeaser(row: DiscoveryTeaserRow): PulseEvent {
  const category = asCategory(row.category);
  return emptyPulseEvent({
    id: row.id,
    category,
    image: resolveDiscoveryImage(row.teaser_image_url, category),
    isTopPick: row.is_top_pick,
    distanceKm: 0,
    isTeaser: true,
    startsAtIso: row.starts_at ?? null,
  });
}

export type DiscoveryFeedMapped = {
  venues: OpenVenue[];
  events: PulseEvent[];
  venueVisibleCount: number;
  venueTotalCount: number;
  eventVisibleCount: number;
  eventTotalCount: number;
  tier: string;
  seedDate: string;
};

export function discoveryFeedQueryKey(city: string) {
  return ["discovery-feed", city] as const;
}

export async function loadMappedDiscoveryFeed(city: string): Promise<DiscoveryFeedMapped> {
  const feed = await fetchDiscoveryFeed(city);
  const venues = [
    ...feed.venues.full.map(mapDiscoveryVenueFull),
    ...feed.venues.teasers.map(mapDiscoveryVenueTeaser),
  ];
  const events = [
    ...feed.live_events.full.map(mapDiscoveryEventFull),
    ...feed.live_events.teasers.map(mapDiscoveryEventTeaser),
  ];
  return {
    venues,
    events,
    venueVisibleCount: feed.venues.visible_count,
    venueTotalCount: feed.venues.total_count,
    eventVisibleCount: feed.live_events.visible_count,
    eventTotalCount: feed.live_events.total_count,
    tier: feed.tier,
    seedDate: feed.seed_date,
  };
}

export function useDiscoveryFeed(city: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: discoveryFeedQueryKey(city),
    queryFn: () => loadMappedDiscoveryFeed(city),
    staleTime: 60_000,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });
}
